import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Moon, Sun, Calculator, RotateCcw } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import GraphDisplay from "./GraphDisplay";
import { conicSectionMath } from "../utils/conicMath";
import { mockExamples } from "../utils/mockData";

const ConicSectionCalculator = () => {
  const { theme, setTheme } = useTheme();
  const [coefficients, setCoefficients] = useState({
    A: 1,
    B: 0,
    C: 1,
    D: 0,
    E: 0,
    F: -1,
  });
  const [results, setResults] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [selectedExample, setSelectedExample] = useState(null);

  const handleInputChange = (key, value) => {
    const numValue = value === "" ? 0 : parseFloat(value);
    setCoefficients(prev => ({ ...prev, [key]: numValue }));
  };

  const calculateResults = () => {
    try {
      const calculation = conicSectionMath.analyzeConicSection(coefficients);
      setResults(calculation);
      setGraphData(calculation.graphData);
    } catch (error) {
      console.error("Calculation error:", error);
      setResults({ error: "計算エラーが発生しました。係数を確認してください。" });
    }
  };

  const loadExample = (example) => {
    setCoefficients(example.coefficients);
    setSelectedExample(example);
  };

  const resetForm = () => {
    setCoefficients({ A: 1, B: 0, C: 1, D: 0, E: 0, F: -1 });
    setResults(null);
    setGraphData(null);
    setSelectedExample(null);
  };

  useEffect(() => {
    calculateResults();
  }, [coefficients]);

  const getCurveTypeColor = (type) => {
    switch (type) {
      case "円": return "bg-blue-500";
      case "楕円": return "bg-green-500";
      case "放物線": return "bg-yellow-500";
      case "双曲線": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-3">
            <Calculator className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              二次曲線解析ツール
            </h1>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            className="transition-all hover:scale-110"
          >
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Left Panel - Input and Results */}
          <div className="space-y-6">
            {/* Input Form */}
            <Card className="backdrop-blur-sm bg-card/90 border-2 hover:border-primary/20 transition-all duration-300">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>係数入力</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetForm}
                    className="hover:bg-destructive/10"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Ax² + Bxy + Cy² + Dx + Ey + F = 0
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(coefficients).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <Label htmlFor={key} className="text-sm font-medium">
                        {key} =
                      </Label>
                      <Input
                        id={key}
                        type="number"
                        step="0.1"
                        value={value}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                        className="transition-all focus:scale-105"
                      />
                    </div>
                  ))}
                </div>
                <Button
                  onClick={calculateResults}
                  className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300"
                >
                  計算実行
                </Button>
              </CardContent>
            </Card>

            {/* Examples */}
            <Card className="backdrop-blur-sm bg-card/90">
              <CardHeader>
                <CardTitle>サンプル例</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-2">
                  {mockExamples.map((example, index) => (
                    <Button
                      key={index}
                      variant={selectedExample === example ? "default" : "outline"}
                      onClick={() => loadExample(example)}
                      className="justify-start text-left h-auto p-3 transition-all hover:scale-105"
                    >
                      <div>
                        <div className="font-medium">{example.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {example.description}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            {results && !results.error && (
              <Card className="backdrop-blur-sm bg-card/90 border-2 border-primary/20">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <span>解析結果</span>
                    <Badge className={`${getCurveTypeColor(results.curveType)} text-white`}>
                      {results.curveType}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="basic" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="basic">基本情報</TabsTrigger>
                      <TabsTrigger value="parameters">パラメータ</TabsTrigger>
                      <TabsTrigger value="transform">変換</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="basic" className="space-y-4">
                      <div className="space-y-2">
                        <p><strong>判別式:</strong> {results.discriminant?.toFixed(4)}</p>
                        <p><strong>曲線タイプ:</strong> {results.curveType}</p>
                        <p><strong>説明:</strong> {results.description}</p>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="parameters" className="space-y-4">
                      {results.parameters && (
                        <div className="space-y-2">
                          {Object.entries(results.parameters).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="font-medium">{key}:</span>
                              <span className="font-mono">
                                {Array.isArray(value) 
                                  ? value.map(v => `(${v.x?.toFixed(2)}, ${v.y?.toFixed(2)})`).join(', ')
                                  : typeof value === 'number' 
                                    ? value.toFixed(4) 
                                    : typeof value === 'object' && value !== null
                                      ? (value.x !== undefined && value.y !== undefined)
                                        ? `(${value.x.toFixed(2)}, ${value.y.toFixed(2)})`
                                        : JSON.stringify(value)
                                      : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="transform" className="space-y-4">
                      {results.standardForm && (
                        <div className="space-y-2">
                          <p><strong>標準形:</strong></p>
                          <p className="font-mono bg-secondary/50 p-2 rounded">
                            {results.standardForm}
                          </p>
                        </div>
                      )}
                      {results.rotationAngle && (
                        <p><strong>回転角:</strong> {results.rotationAngle.toFixed(2)}°</p>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}

            {results?.error && (
              <Card className="backdrop-blur-sm bg-card/90 border-2 border-destructive/20">
                <CardContent className="pt-6">
                  <p className="text-destructive text-center">{results.error}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - Graph */}
          <Card className="backdrop-blur-sm bg-card/90 border-2 hover:border-primary/20 transition-all duration-300">
            <CardHeader>
              <CardTitle>グラフ表示</CardTitle>
            </CardHeader>
            <CardContent>
              <GraphDisplay graphData={graphData} theme={theme} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ConicSectionCalculator;